import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import { VISITOR_IDENTIFIED_CONTACT_INFO_STORAGE_KEY } from "@jugl-web/utils/storage";
import { useLocalStorage } from "@jugl-web/utils/hooks/useStorage";
import { Button, PlainButton } from "@jugl-web/ui-kit";
import { useClientSupportPageWithSelectedConversation } from "@web-src/modules/client-support/pages/ClientSupportPage/providers/ClientSupportPageProvider/useClientSupportPageWithSelectedConversation";
import { useSubscriptionV2 } from "@web-src/modules/cx/providers/SubscriptionV2Provider";
import { getPlanTierIndex } from "@web-src/modules/cx/pages/CXPlansPage/utils/planTierUtils";
import { PlanTierIndex } from "@jugl-web/rest-api";
import { useEntityProvider } from "@web-src/modules/entities/providers/EntityProvider/useEntityProvider";
import platfromsImage from "./assets/platforms.png";
import { ReactComponent as CloseIcon } from "./assets/close.svg";
import { ReactComponent as ArrowIcon } from "./assets/arrow.svg";
import {
  getVisitorIntelligenceDataPointsSnapshot,
  hasVisitorIntelligenceData,
} from "../../utils";

type VisitorIntelligenceDataPointsStorage = Record<string, string[]>;

const hasVisitorIntelligenceDataPointsChanged = (
  previousSnapshot: string[] | undefined,
  currentSnapshot: string[]
) => JSON.stringify(previousSnapshot ?? []) !== JSON.stringify(currentSnapshot);

export const VisitorIdentifiedBanner: React.FC = () => {
  const { t } = useTranslations();
  const { entityId } = useEntityProvider();
  const { currentPlan } = useSubscriptionV2();
  const [isOpen, setIsOpen] = useState(false);
  const { selectedConversation, setOpenedContactInfo } =
    useClientSupportPageWithSelectedConversation();

  const hasBusinessPlanOrAbove = useMemo(
    () =>
      currentPlan && getPlanTierIndex(currentPlan) >= PlanTierIndex.business,
    [currentPlan]
  );

  const [storedDataPointsSnapshots, setStoredDataPointsSnapshots] =
    useLocalStorage<VisitorIntelligenceDataPointsStorage>(
      VISITOR_IDENTIFIED_CONTACT_INFO_STORAGE_KEY,
      {}
    );

  const conversationId = selectedConversation.conv_id;
  const contactInfo = useMemo(
    () => selectedConversation.contact_info ?? {},
    [selectedConversation.contact_info]
  );

  const visitorIntelligenceDataPointsSnapshot = useMemo(
    () => getVisitorIntelligenceDataPointsSnapshot(contactInfo),
    [contactInfo]
  );

  const hasVisitorIntelligence = useMemo(
    () => hasVisitorIntelligenceData(contactInfo),
    [contactInfo]
  );

  const dismissBanner = useCallback(() => {
    if (!entityId) {
      setIsOpen(false);
      return;
    }

    const storageKey = `${entityId}-${conversationId}`;

    setStoredDataPointsSnapshots((previous) => ({
      ...previous,
      [storageKey]: visitorIntelligenceDataPointsSnapshot,
    }));
    setIsOpen(false);
  }, [
    conversationId,
    entityId,
    setStoredDataPointsSnapshots,
    visitorIntelligenceDataPointsSnapshot,
  ]);

  useEffect(() => {
    setIsOpen(false);
  }, [conversationId]);

  useEffect(() => {
    if (!entityId) {
      return;
    }

    const storageKey = `${entityId}-${conversationId}`;
    const storedDataPointsSnapshot = storedDataPointsSnapshots[storageKey];
    const hasDataPointsChanged = hasVisitorIntelligenceDataPointsChanged(
      storedDataPointsSnapshot,
      visitorIntelligenceDataPointsSnapshot
    );

    if (
      hasBusinessPlanOrAbove &&
      hasVisitorIntelligence &&
      hasDataPointsChanged
    ) {
      setIsOpen(true);
    }
  }, [
    conversationId,
    entityId,
    hasBusinessPlanOrAbove,
    hasVisitorIntelligence,
    storedDataPointsSnapshots,
    visitorIntelligenceDataPointsSnapshot,
  ]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="flex h-[76px] items-center justify-between gap-4 border-0 border-y border-solid border-[#F2F2F2] px-7">
      <div className="flex items-center gap-6">
        <img src={platfromsImage} alt="" className="h-[76px]" />
        <div className="flex flex-col gap-0.5">
          <span className="text-dark font-secondary text-[15px] font-medium leading-[150%]">
            {t({
              id: "contacts-page.visitor-identified",
              defaultMessage: "Visitor Identified",
            })}
          </span>
          <span className="text-dark-600 font-secondary text-sm leading-[21px]">
            {t({
              id: "contacts-page.visitor-identified-additional-info",
              defaultMessage:
                "We found some additional info about this customer",
            })}
          </span>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Button
          variant="secondary"
          size="sm"
          iconEnd={<ArrowIcon />}
          onClick={() => {
            setOpenedContactInfo({
              conversationId,
              variant: "visitorIntelligence",
            });
            dismissBanner();
          }}
        >
          {t({
            id: "contacts-page.check-it-out",
            defaultMessage: "Check it out",
          })}
        </Button>
        <PlainButton
          className="border-grey-300 ring-grey-300 flex h-8 w-8 items-center justify-center rounded-lg border border-solid ring-inset transition-all hover:ring-1"
          onClick={dismissBanner}
        >
          <CloseIcon />
        </PlainButton>
      </div>
    </div>
  );
};
